import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Content } from "../../components/wrappers/Content";
import { useFetch } from "../../lib/useFetch";
import { BucketLines, CurrencyBucket, fmtMoney, Panel, Stat } from "./shared";
import { XeroConnectionPanel } from "./XeroConnectionPanel";

/** Per-category buckets + the £ rollup (GBP is the base reporting currency). */
type MoneyGroup = { buckets: CurrencyBucket[]; totalGbp: number; allConverted: boolean };

const gbp = (g?: MoneyGroup) =>
  g == null ? undefined : `${g.allConverted ? "" : "≥ "}${fmtMoney(g.totalGbp, "GBP")}`;

/** Finance home (Phase C): instalment money in/out from the schedule data helm-app
 *  already holds. Receivables/payables/commission screens arrive in Phase D. */

type FinanceHomeData = {
  overdue: MoneyGroup;
  dueSoon: MoneyGroup;
  payablesSoon: MoneyGroup;
  commissionPipeline: MoneyGroup;
};

export default function FinanceHome() {
  const { fetch } = useFetch();
  const [data, setData] = useState<FinanceHomeData | null>(null);

  useEffect(() => {
    fetch<{ data: FinanceHomeData }>("/api/offers/workspace/home-finance")
      .then((r) => setData(r.data))
      .catch(() => null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const overdueCount = data?.overdue.buckets.reduce((s, b) => s + b.count, 0);

  return (
    <Content title="Finance">
      <div className="flex flex-col gap-6 p-1">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Stat
            label="Overdue client instalments"
            value={gbp(data?.overdue)}
            detail={<BucketLines buckets={data?.overdue.buckets ?? undefined} empty="nothing overdue" />}
            tone={overdueCount ? "bad" : "good"}
          />
          <Stat
            label="Client instalments due ≤14d"
            value={gbp(data?.dueSoon)}
            detail={<BucketLines buckets={data?.dueSoon.buckets ?? undefined} empty="none due" />}
          />
          <Stat
            label="Supplier payments due ≤14d"
            value={gbp(data?.payablesSoon)}
            detail={<BucketLines buckets={data?.payablesSoon.buckets ?? undefined} empty="none due" />}
          />
          <Stat
            label="Commission pipeline"
            value={gbp(data?.commissionPipeline)}
            detail={<BucketLines buckets={data?.commissionPipeline.buckets ?? undefined} empty="no upcoming convert bookings" />}
            tone="good"
          />
        </div>

        <XeroConnectionPanel />

        <Panel title="Where to work">
          <div className="flex flex-col gap-2 text-sm">
            <Link className="text-blue-600 hover:underline" to="/payments">
              Payments — record and reconcile instalments
            </Link>
            <Link className="text-blue-600 hover:underline" to="/payments-summary">
              Payments summary
            </Link>
            <Link className="text-blue-600 hover:underline" to="/bookings">
              Bookings — per-charter money view
            </Link>
            <p className="mt-1 text-xs text-slate-400">
              Receivables, payables and the commission queue land here with the Phase D money screens.
            </p>
          </div>
        </Panel>
      </div>
    </Content>
  );
}
